import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Form from 'components/forms/form';
import Section from 'components/layout/section';
import TextInput from 'components/inputs/textInput';
import TextareaInput from 'components/inputs/textareaInput';
import CustomerSelector from '@/components/searchAndSelect/CustomerSelector';
import SupplierSelector from '@/components/searchAndSelect/SupplierSelector';
import Button from 'components/inputs/button';
import ButtonGroup from 'components/buttons/buttonGroup';
import ProcessingButton from 'components/inputs/processingButton';
import FormErrors from 'components/formParts/formErrors';
import Callout from 'components/callout/callout';
import { ReactComponent as SaveIcon } from 'assets/icons/save.svg';
import { ReactComponent as CloseIcon } from 'assets/icons/close.svg';
import useStorages from '@/hooks/useStorages';
import useStorageLimits from '@/hooks/useStorageLimits';
import useTransactions from '@/hooks/useTransactions';
import { TransactionType, TransactionWrite } from 'types';
import { decodeFirebaseError } from '@/utils/firebaseHelpers';

interface EditTransactionFormProps {
    storageId: string;
    transactionId: string;
    transaction: TransactionWrite;
}

const EditTransactionForm: React.FC<EditTransactionFormProps> = ({ 
    storageId, 
    transactionId,
    transaction 
}) => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const [id, setId] = useState<number | undefined>(transaction.transactionId);
    const [description, setDescription] = useState(transaction.description || '');
    const [selectedCustomerId, setSelectedCustomerId] = useState(transaction.customer?.customerId || '');
    const [selectedSupplierId, setSelectedSupplierId] = useState(transaction.supplier?.supplierId || '');
    
    const [errors, setErrors] = useState<string[]>([]);

    const { updateTransactionAsync, isUpdating } = useTransactions({ storageId });
    const { storage, isCustomersAndSuppliersEnabled } = useStorages({ storageId });
    const { canUseCustomers, canUseSuppliers } = useStorageLimits({ storagePlan: storage?.settings?.storagePlan });
    const showSupplierField = canUseSuppliers && isCustomersAndSuppliersEnabled();
    const showCustomerField = canUseCustomers && isCustomersAndSuppliersEnabled();

    // Check if transaction can be edited (only drafts / not finalized)
    const canEdit = transaction.isDraft === true && transaction.isFinalized === false;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!canEdit) return;
        
        setErrors([]);

        const updateData: TransactionWrite = {
            ids: {
                storageId,
                transactionId,
            },
            transactionId: id as number,
            description: description || undefined,
            customer: showCustomerField && selectedCustomerId ? { customerId: selectedCustomerId } : undefined,
            supplier: showSupplierField && selectedSupplierId ? { supplierId: selectedSupplierId } : undefined,
            // Keep as draft when editing via this form
            isDraft: true,
            isFinalized: false,
            _version: 1,
            type: transaction.type as TransactionType,
        }

        try {
            await updateTransactionAsync(updateData);
            // Navigate back to transaction view
            navigate(`/storage/transactions/${transactionId}`);
        } catch (error) {
            const errorMessage = decodeFirebaseError(error instanceof Error ? error.message : String(error));
            setErrors([errorMessage]);
        }
    };

    if (!canEdit) {
        return (
            <Section>
                <Callout type="warning" id="transactionNotEditable">
					{t('transactionNotEditableInfo', 'This transaction cannot be edited because it has been published.')}
                </Callout>
            </Section>
        );
    }

    return (
        <Form
            className={`group`}
            onSubmit={handleSubmit}
            isLoading={isUpdating}
        >

            <Section>

                <Callout type="info" id="editTransactionInfo">
					{t('editTransactionInfo', 'You can edit this draft transaction. Once published, it cannot be edited anymore.')}
                </Callout>

                <TextInput
                    label={t('labelId')}
                    value={transaction.transactionId?.toString() || ''}
                    disabled
                    onChange={() => {}} // Required but disabled
                />

                <TextareaInput
                    label={t('labelDescription')}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                />

                {transaction.type === TransactionType.INCOMING && showSupplierField && (
                    <div className="input-holder-wide">
                        <label>{t('labelSupplier')}</label>
                        <SupplierSelector
                            storageId={storageId}
                            selectedSupplierId={selectedSupplierId}
                            onSupplierSelect={setSelectedSupplierId}
                            placeholder={t('selectSupplier', 'Select a supplier')}
                        />
                    </div>
                )}

                {transaction.type === TransactionType.OUTGOING && showCustomerField && (
                    <div className="input-holder-wide">
                        <label>{t('labelCustomer')}</label>
                        <CustomerSelector
                            storageId={storageId}
                            selectedCustomerId={selectedCustomerId}
                            onCustomerSelect={setSelectedCustomerId}
                            placeholder={t('selectCustomer', 'Select a customer')}
                        />
                    </div>
                )}

                <ButtonGroup>
                    {isUpdating ? (
                        <ProcessingButton design="primary" />
                    ) : (
                        <>
                            <Button wideOnMobile 
                                design="white" 
                                type="button"
                                onClick={() => navigate(`/storage/transactions/${transactionId}`)}
                            >
                                <CloseIcon />
                                <span>{t('buttonCancel')}</span>
                            </Button>
                            <Button wideOnMobile 
                                design="primary" 
                                type="submit"
                            >
                                <SaveIcon />
                                <span>{t('buttonSaveAsDraft', 'Save as Draft')}</span>
                            </Button>
                        </>
                    )}
                </ButtonGroup>

            </Section>

            {errors.length > 0 && <FormErrors errors={errors} />}
        </Form>
    );
};

export default EditTransactionForm;
